(function () {
  'use strict';

  var railControllers = new WeakMap();
  var modeNavControllers = new WeakMap();
  var modeNavRoots = new Set();
  var navRefreshFrame = null;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function rootsWithin(scope, selector) {
    var roots = [];
    if (!scope) return roots;
    if (scope.matches && scope.matches(selector)) roots.push(scope);
    if (scope.querySelectorAll) {
      roots = roots.concat(Array.prototype.slice.call(scope.querySelectorAll(selector)));
    }
    return roots;
  }

  function destroyRail(root) {
    var controller = railControllers.get(root);
    if (!controller) return;
    controller.destroy();
    railControllers.delete(root);
  }

  function setupRail(root) {
    if (!root || railControllers.has(root)) return;

    var viewport = root.querySelector('[data-rail]');
    var previousButton = root.querySelector('[data-rail-prev]');
    var nextButton = root.querySelector('[data-rail-next]');
    if (!viewport || !previousButton || !nextButton) return;

    function step() {
      var card = root.querySelector('.natridy-recipe-card');
      var track = root.querySelector('.natridy-recipe-rail__track');
      var gap = track ? parseFloat(window.getComputedStyle(track).columnGap) || 0 : 0;
      return card ? card.offsetWidth + gap : 320;
    }

    function updateButtons() {
      var maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      previousButton.disabled = viewport.scrollLeft <= 4;
      nextButton.disabled = viewport.scrollLeft >= maxScroll - 4;
    }

    function previousHandler() {
      viewport.scrollBy({ left: -step(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    function nextHandler() {
      viewport.scrollBy({ left: step(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    previousButton.addEventListener('click', previousHandler);
    nextButton.addEventListener('click', nextHandler);
    viewport.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    root.dataset.natridyRailReady = 'true';
    updateButtons();

    railControllers.set(root, {
      destroy: function () {
        previousButton.removeEventListener('click', previousHandler);
        nextButton.removeEventListener('click', nextHandler);
        viewport.removeEventListener('scroll', updateButtons);
        window.removeEventListener('resize', updateButtons);
        delete root.dataset.natridyRailReady;
      }
    });
  }

  function destroyModeNav(root) {
    var controller = modeNavControllers.get(root);
    if (controller) controller.destroy();
    modeNavControllers.delete(root);
    modeNavRoots.delete(root);
  }

  function setupModeNav(root) {
    if (!root) return;
    destroyModeNav(root);

    var chips = Array.prototype.slice.call(root.querySelectorAll('.ngr-chip'));
    var chipContainer = root.querySelector('.ngr-nav__chips');
    if (!chips.length || !chipContainer) return;

    var clickBindings = [];
    var observer = null;
    var targetPairs = chips.map(function (chip) {
      var anchor = chip.getAttribute('href');
      var targetId = anchor && anchor.charAt(0) === '#' ? anchor.slice(1) : '';
      var target = targetId ? document.getElementById(targetId) : null;
      return target ? { chip: chip, target: target } : null;
    }).filter(Boolean);

    function activate(chip) {
      chips.forEach(function (item) {
        var isActive = item === chip;
        item.classList.toggle('ngr-chip--active', isActive);
        if (isActive) item.setAttribute('aria-current', 'location');
        else item.removeAttribute('aria-current');
      });

      var containerRect = chipContainer.getBoundingClientRect();
      var chipRect = chip.getBoundingClientRect();
      var chipStart = chipRect.left - containerRect.left + chipContainer.scrollLeft;
      var chipEnd = chipStart + chip.offsetWidth;
      var visibleStart = chipContainer.scrollLeft;
      var visibleEnd = visibleStart + chipContainer.clientWidth;
      if (chipStart < visibleStart || chipEnd > visibleEnd) {
        var centeredLeft = chipStart - (chipContainer.clientWidth - chip.offsetWidth) / 2;
        chipContainer.scrollTo({
          left: Math.max(0, centeredLeft),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      }
    }

    chips.forEach(function (chip) {
      var handler = function () { activate(chip); };
      chip.addEventListener('click', handler);
      clickBindings.push({ chip: chip, handler: handler });
    });

    if ('IntersectionObserver' in window && targetPairs.length) {
      observer = new IntersectionObserver(function (entries) {
        var visible = entries.filter(function (entry) { return entry.isIntersecting; });
        if (!visible.length) return;
        visible.sort(function (a, b) { return b.intersectionRatio - a.intersectionRatio; });
        var matchingPair = targetPairs.find(function (pair) { return pair.target === visible[0].target; });
        if (matchingPair) activate(matchingPair.chip);
      }, { rootMargin: '-25% 0px -60% 0px', threshold: [0, 0.1, 0.5] });

      targetPairs.forEach(function (pair) { observer.observe(pair.target); });
    }

    root.dataset.ngrNavReady = 'true';
    modeNavRoots.add(root);
    modeNavControllers.set(root, {
      destroy: function () {
        if (observer) observer.disconnect();
        clickBindings.forEach(function (binding) {
          binding.chip.removeEventListener('click', binding.handler);
        });
        delete root.dataset.ngrNavReady;
      }
    });
  }

  function refreshModeNavs() {
    Array.from(modeNavRoots).forEach(function (root) {
      if (root.isConnected) setupModeNav(root);
      else destroyModeNav(root);
    });
  }

  function scheduleModeNavRefresh() {
    if (navRefreshFrame) window.cancelAnimationFrame(navRefreshFrame);
    navRefreshFrame = window.requestAnimationFrame(function () {
      navRefreshFrame = null;
      refreshModeNavs();
    });
  }

  function setupWithin(scope) {
    rootsWithin(scope, '[data-natridy-recipe-rail]').forEach(setupRail);
    rootsWithin(scope, '[data-natridy-gelato-mode-nav]').forEach(setupModeNav);
  }

  function teardownWithin(scope) {
    rootsWithin(scope, '[data-natridy-recipe-rail]').forEach(destroyRail);
    rootsWithin(scope, '[data-natridy-gelato-mode-nav]').forEach(destroyModeNav);
  }

  function containsCollection(scope) {
    return rootsWithin(scope, '.ngr-col').length > 0;
  }

  function initialize() {
    setupWithin(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    setupWithin(event.target);
    if (containsCollection(event.target)) scheduleModeNavRefresh();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardownWithin(event.target);
    if (containsCollection(event.target)) scheduleModeNavRefresh();
  });

  document.addEventListener('shopify:section:reorder', scheduleModeNavRefresh);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
